import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FiCreditCard, FiPackage, FiX } from 'react-icons/fi'

import { Button } from '~/components/Button'
import { Modal } from '~/components/Modal'
import { CardForm } from '~/forms/Sales/Checkout/CardForm'
import { CardDataFormValues } from '~/forms/Sales/Checkout/CardForm/schema'
import { PaymentGatewayRenderer } from '~/components/PaymentGatewayRenderer'
import { KiwifyCheckoutButton } from '~/components/KiwifyCheckoutButton'
import { purchaseLicenseExtension } from '~/modules/sales/licenseExtensions/services'
import { AvailableLicenseExtension } from '~/modules/sales/licenseExtensions/types'
import { numberToBRL } from '~/modules/sales/services'
import { insertToast } from '~/modules/toasts/services'
import { api } from '~/utils/api'

import { subscriptionsContext } from '../..'

type Props = {
  extension: AvailableLicenseExtension
  termStart: string
  termEnd: string
  visible: boolean
  onClose(): void
  onSuccess(): void
}

// Minimal PaymentPlan shim — license extensions are always credit card, 1x
const LICENSE_PAYMENT_PLAN = {
  id: 'license-ext',
  name: '',
  externalId: '',
  provider: 'pagarme' as const,
  value: 0,
  interval: 'month' as const,
  intervalCount: 1,
  licensesAmount: 0,
  metaData: { items: [], paymentMethods: ['credit_card' as const] },
  active: true,
  visible: true,
  createdBy: '',
  createdAt: '',
  updatedAt: '',
  installments: 1,
}

export const PurchaseModal: React.FC<Props> = ({
  extension,
  termStart,
  termEnd,
  visible,
  onClose,
  onSuccess,
}) => {
  const { subscription } = useContext(subscriptionsContext)
  const [useExistingCard, setUseExistingCard] = useState(true)
  const [cardLastFour, setCardLastFour] = useState<string>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!visible || !subscription?.externalId) return
    const controller = new AbortController()
    api
      .get<{ lastFourDigits: string }>(
        `/personal-trainers/subscriptions/${subscription.externalId}/card`,
        { signal: controller.signal },
      )
      .then(({ data }) => setCardLastFour(data.lastFourDigits))
      .catch(() => {
        // If we can't fetch, fall back to new card
        setUseExistingCard(false)
      })
    return () => controller.abort()
  }, [visible, subscription?.externalId])

  // Reset to existing-card mode each time the modal opens
  useEffect(() => {
    if (visible) setUseExistingCard(true)
  }, [visible])

  const remainingDays = useMemo(() => {
    const today = new Date()
    const end = new Date(termEnd)
    return Math.max(0, Math.ceil((end.getTime() - today.getTime()) / 86_400_000))
  }, [termEnd])

  const daysInCycle = useMemo(() => {
    const start = new Date(termStart)
    const end = new Date(termEnd)
    return Math.ceil((end.getTime() - start.getTime()) / 86_400_000)
  }, [termStart, termEnd])

  const handleSuccess = useCallback(() => {
    insertToast({
      duration: 6,
      title: 'Pagamento em processamento',
      message: 'Suas licenças serão liberadas assim que o pagamento for confirmado.',
    })
    onSuccess()
    onClose()
  }, [onSuccess, onClose])

  const handleError = useCallback((e: Error) => {
    insertToast({
      duration: 4,
      title: 'Ops',
      message: e.message,
      type: 'error',
    })
  }, [])

  const handleSubmitExisting = useCallback(async () => {
    setSubmitting(true)
    await purchaseLicenseExtension({
      licenseExtensionId: extension.id,
      useExistingCard: true,
    })
      .then(() => handleSuccess())
      .catch(handleError)
      .finally(() => setSubmitting(false))
  }, [extension.id, handleSuccess, handleError])

  const handleSubmitNewCard = useCallback(
    async (card: CardDataFormValues) => {
      await purchaseLicenseExtension({
        licenseExtensionId: extension.id,
        card,
      })
        .then(() => handleSuccess())
        .catch(handleError)
    },
    [extension.id, handleSuccess, handleError],
  )

  return (
    <Modal visible={visible}>
      {/* Header */}
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center gap-3'>
          <div className='p-2 bg-gpa-blue-50 rounded-lg text-gpa-blue-500'>
            <FiPackage size={20} />
          </div>
          <h2 className='text-xl font-semibold'>{extension.name}</h2>
        </div>
        <button
          className='bg-white hover:bg-gpa-gray-50 p-1.5 rounded-full transition-colors'
          onClick={onClose}
        >
          <FiX size={24} />
        </button>
      </div>

      {/* Price summary */}
      <div className='flex flex-col gap-3 bg-gpa-blue-50 rounded-xl p-5 mb-6'>
        <div className='flex justify-between text-sm'>
          <span>Licenças adicionadas</span>
          <span className='font-semibold'>{extension.licensesAmount} licenças</span>
        </div>
        <div className='flex justify-between text-sm'>
          <span>Preço cheio do pacote</span>
          <span>{numberToBRL(extension.price / 100)}</span>
        </div>
        <div className='flex justify-between text-sm'>
          <span>
            Dias restantes no ciclo{' '}
            <span className='text-xs text-gpa-gray-800'>
              ({remainingDays} de {daysInCycle} dias)
            </span>
          </span>
          <span className='text-gpa-green font-medium'>
            -{numberToBRL((extension.price - extension.proratedPrice) / 100)}
          </span>
        </div>
        <div className='border-t-2 border-gpa-blue-500 pt-3 flex justify-between'>
          <span className='font-semibold'>Total a pagar agora</span>
          <span className='text-xl font-bold text-gpa-blue-500'>
            {numberToBRL(extension.proratedPrice / 100)}
          </span>
        </div>
        <p className='text-xs text-gpa-gray-800 mt-1'>
          Na renovação do plano, o valor cheio de{' '}
          {numberToBRL(extension.price / 100)} será cobrado automaticamente.
        </p>
      </div>

      <PaymentGatewayRenderer
        pagarmeComponent={
          <>
            {/* Payment method toggle */}
            <h3 className='text-sm font-semibold mb-3'>Forma de pagamento</h3>
            <div className='flex gap-2 mb-6'>
              <button
                type='button'
                onClick={() => setUseExistingCard(true)}
                className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border-2
                  text-sm font-medium transition-colors duration-150
                  ${
                    useExistingCard
                      ? 'border-gpa-blue-500 bg-gpa-blue-50 text-gpa-blue-500'
                      : 'border-gpa-gray-500 bg-white'
                  }`}
              >
                <FiCreditCard size={16} />
                <span className='text-left'>
                  Cartão atual
                  {cardLastFour ? (
                    <span className='block text-xs font-normal text-gpa-gray-800'>
                      termina em {cardLastFour}
                    </span>
                  ) : null}
                </span>
              </button>
              <button
                type='button'
                onClick={() => setUseExistingCard(false)}
                className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-lg border-2
                  text-sm font-medium transition-colors duration-150
                  ${
                    !useExistingCard
                      ? 'border-gpa-blue-500 bg-gpa-blue-50 text-gpa-blue-500'
                      : 'border-gpa-gray-500 bg-white'
                  }`}
              >
                <FiCreditCard size={16} />
                <span className='text-left'>
                  Outro cartão
                  <span className='block text-xs font-normal text-gpa-gray-800'>
                    inserir dados
                  </span>
                </span>
              </button>
            </div>

            {/* Existing card confirm */}
            {useExistingCard ? (
              <Button
                className='w-full'
                loading={submitting}
                disabled={submitting}
                loadingMessage='Finalizando pagamento...'
                onClick={handleSubmitExisting}
              >
                Finalizar Pedido
              </Button>
            ) : (
              <CardForm
                onSubmit={handleSubmitNewCard}
                paymentPlan={LICENSE_PAYMENT_PLAN}
              />
            )}
          </>
        }
        kiwifyComponent={
          <div className="mt-4 border-t-2 border-gpa-blue-500 pt-4">
            <KiwifyCheckoutButton extensionId={extension.id} />
          </div>
        }
      />
    </Modal>
  )
}
